import React, { forwardRef } from 'react';
import { ReactComponent as AddUserIcon } from '@/img/button-icon-adduser.svg';
import { ReactComponent as ContactIcon } from '@/img/button-icon-contact.svg';
import { ReactComponent as ContactVoiceIcon } from '@/img/button-icon-contact-voice.svg';
import { ReactComponent as DeleteIcon } from '@/img/button-icon-delete.svg';
import { ReactComponent as DocIcon } from '@/img/button-icon-doc.svg';
import { ReactComponent as DownloadIcon } from '@/img/button-icon-download.svg';
import { ReactComponent as EditIcon } from '@/img/button-icon-edit.svg';
import { ReactComponent as GoIcon } from '@/img/button-icon-go.svg';
import { ReactComponent as InfoIcon } from '@/img/button-icon-info.svg';
import { ReactComponent as UserInfoIcon } from '@/img/button-icon-userinfo.svg';
import { ReactComponent as WarningIcon } from '@/img/button-icon-warning.svg';
import { ReactComponent as UnlinkIcon } from '@/img/button-icon-unlink.svg';
import './UIIconButton.scss';

export type UIIconType =
  | 'adduser'
  | 'contact'
  | 'contact-voice'
  | 'delete'
  | 'doc'
  | 'download'
  | 'edit'
  | 'go'
  | 'info'
  | 'userinfo'
  | 'warning'
  | 'unlink';

export interface UIIconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  /**
   * Варіант кольору кнопки
   */
  variant?: 'primary' | 'success' | 'info' | 'danger' | 'secondary';

  /**
   * Розмір кнопки
   */
  size?: 'small' | 'medium';

  /**
   * Тип іконки (з предустановленого набору)
   */
  iconType?: UIIconType;

  /**
   * Кастомна іконка (має пріоритет над iconType)
   */
  icon?: React.ReactNode;

  /**
   * Текст кнопки (якщо вказаний, кнопка стає текстово-іконочною)
   */
  text?: string;

  /**
   * Додатковий CSS клас
   */
  className?: string;

  /**
   * Вимкнений стан
   */
  disabled?: boolean;
}

// Мапа іконок
const iconMap: Record<UIIconType, React.ComponentType> = {
  adduser: AddUserIcon,
  contact: ContactIcon,
  'contact-voice': ContactVoiceIcon,
  delete: DeleteIcon,
  doc: DocIcon,
  download: DownloadIcon,
  edit: EditIcon,
  go: GoIcon,
  info: InfoIcon,
  userinfo: UserInfoIcon,
  warning: WarningIcon,
  unlink: UnlinkIcon,
};

// Мокова іконка для демонстрації
const MockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2L2 7L12 12L22 7L12 2Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const baseClass = 'UIIconButton';

const UIIconButton = forwardRef<HTMLButtonElement, UIIconButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      iconType = 'edit',
      icon,
      text,
      className = '',
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const buttonClasses = [
      baseClass,
      `${baseClass}--${variant}`,
      `${baseClass}--${size}`,
      text && `${baseClass}--with-text`,
      disabled && `${baseClass}--disabled`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Визначаємо яку іконку рендерити
    const renderIcon = () => {
      // Кастомна іконка має пріоритет
      if (icon) {
        return icon;
      }

      // Інакше використовуємо іконку з мапи
      const IconComponent = iconMap[iconType];
      return IconComponent ? <IconComponent /> : <MockIcon />;
    };

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={disabled}
        {...props}
      >
        <span className={`${baseClass}__icon`}>
          {renderIcon()}
        </span>
        {text && (
          <span className={`${baseClass}__text`}>
            {text}
          </span>
        )}
      </button>
    );
  },
);

UIIconButton.displayName = 'UIIconButton';

export default UIIconButton;